import { useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';

import { isEqual } from 'lodash';

import { useBallAnimation } from '../../../hooks/useBallAnimation';
import { useSocketDpdg } from '../../../hooks/useSocketDpdg';
import { Trajectory } from '../../../hooks/useDpdgState';

import forest from '../../../assets/images/dpdg/forest.jpg';

import { DpdgControlPanel } from './components/dpdg-control-panel';

const PROGRESS_HIDE_DELAY = 400;
const imageBlobCache = new Map<string, Blob>();

const DIRECTUS_DOMAIN = (
  (import.meta.env.VITE_REACT_APP_DIRECTUS_DOMAIN as string | undefined) ?? 'https://d-t1.zingizi.pro'
).replace(/\/$/, '');

const resolveAssetUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${DIRECTUS_DOMAIN}/assets/${path}`;
};

interface IBallProps {
  isPsychologist: boolean;
  roomId: string;
  trajectory: Trajectory;
  bgColor: string;
  ballColor: string;
  velocity: number;
  backgroundType: 'none' | 'video' | 'image';
  backgroundValue: string;
  ballSize: number;
  changeTrajectory?: (trajectory: Trajectory) => void;
  sendDpdgMessage?: (action: string, data: Record<string, unknown>) => void;
  useDataChannelToolTransport?: boolean;
  /** Клиент: движение шара с стороны психолога (LiveKit) */
  liveKitBallMoving?: boolean;
  /** Мобильный 1:1: компактная панель управления психолога */
  compactControls?: boolean;
}

const DpDgBall: React.FC<IBallProps> = ({
  isPsychologist,
  roomId,
  trajectory,
  bgColor,
  ballColor,
  velocity,
  backgroundType,
  backgroundValue,
  ballSize,
  changeTrajectory,
  sendDpdgMessage,
  useDataChannelToolTransport,
  liveKitBallMoving,
  compactControls = false,
}) => {
  const [moving, setMoving] = useState(false);
  const [bgImageLoaded, setBgImageLoaded] = useState<boolean | null>(true);
  const [mediaLoadingProgress, setMediaLoadingProgress] = useState<number | null>(null);
  const [mediaReady, setMediaReady] = useState<boolean>(
    () => !(backgroundType === 'video' || (backgroundType === 'image' && backgroundValue)),
  );
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaReadyTimeoutRef = useRef<number | null>(null);
  const imageFetchAbortRef = useRef<AbortController | null>(null);
  const imageObjectUrlRef = useRef<string | null>(null);

  useBallAnimation({
    canvasRef,
    moving,
    velocity,
    trajectory,
    ballColor,
    bgColor,
    backgroundType,
    ballSize,
  });

  const { emitChange } = useSocketDpdg(
    isPsychologist,
    roomId,
    (data) => {
      if (typeof data.moving === 'boolean') {
        setMoving((prev) => (data.moving === undefined ? prev : data.moving));
      }
    },
    {
      socketDisabled: Boolean(useDataChannelToolTransport),
      publishAction: useDataChannelToolTransport && isPsychologist && sendDpdgMessage ? sendDpdgMessage : undefined,
    },
  );

  useEffect(() => {
    if (!useDataChannelToolTransport || isPsychologist || typeof liveKitBallMoving !== 'boolean') return;
    setMoving(liveKitBallMoving);
  }, [liveKitBallMoving, useDataChannelToolTransport, isPsychologist]);

  const prevPayloadRef = useRef<{
    moving: boolean;
    velocity: number;
    ballColor: string;
    bgColor: string;
    ballSize: number;
    backgroundType: 'none' | 'video' | 'image';
    backgroundValue: string;
    trajectory: Trajectory;
  } | null>(null);

  const revokeImageObjectUrl = () => {
    if (imageObjectUrlRef.current) {
      URL.revokeObjectURL(imageObjectUrlRef.current);
      imageObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!isPsychologist) return;

    const currentPayload = {
      moving,
      velocity,
      ballColor,
      bgColor,
      ballSize,
      backgroundType,
      backgroundValue,
      trajectory,
    };

    if (!isEqual(prevPayloadRef.current, currentPayload)) {
      emitChange(moving ? 'ball_start' : 'ball_stop', currentPayload);
      prevPayloadRef.current = currentPayload;
    }
  }, [
    isPsychologist,
    moving,
    velocity,
    ballColor,
    bgColor,
    ballSize,
    backgroundType,
    backgroundValue,
    trajectory,
    emitChange,
  ]);

  useEffect(() => {
    if (backgroundType === 'image' && backgroundValue) {
      setBgImageLoaded(true);
    }
  }, [backgroundType, backgroundValue]);

  useEffect(() => {
    if (backgroundType === 'video' || (backgroundType === 'image' && backgroundValue)) {
      setMediaReady(false);
    } else {
      setMediaReady(true);
    }
  }, [backgroundType, backgroundValue]);

  useEffect(() => {
    if (backgroundType === 'video' && backgroundValue) {
      setMediaLoadingProgress(0);
    } else if (backgroundType !== 'image') {
      setMediaLoadingProgress(null);
    }
  }, [backgroundType, backgroundValue]);

  useEffect(() => {
    if (mediaLoadingProgress !== null && mediaLoadingProgress >= 1) {
      if (mediaReadyTimeoutRef.current) {
        window.clearTimeout(mediaReadyTimeoutRef.current);
      }
      mediaReadyTimeoutRef.current = window.setTimeout(() => {
        setMediaLoadingProgress(null);
        mediaReadyTimeoutRef.current = null;
      }, PROGRESS_HIDE_DELAY);
    }

    return () => {
      if (mediaReadyTimeoutRef.current) {
        window.clearTimeout(mediaReadyTimeoutRef.current);
        mediaReadyTimeoutRef.current = null;
      }
    };
  }, [mediaLoadingProgress]);

  useEffect(() => {
    if (backgroundType !== 'image' || !backgroundValue) {
      if (imageFetchAbortRef.current) {
        imageFetchAbortRef.current.abort();
        imageFetchAbortRef.current = null;
      }
      revokeImageObjectUrl();
      setImageSrc(null);
      setMediaLoadingProgress(null);
      return;
    }

    setBgImageLoaded(true);

    const cachedBlob = imageBlobCache.get(backgroundValue);
    if (cachedBlob) {
      revokeImageObjectUrl();
      const cachedUrl = URL.createObjectURL(cachedBlob);
      imageObjectUrlRef.current = cachedUrl;
      setImageSrc(cachedUrl);
      setMediaLoadingProgress(1);
      if (imageFetchAbortRef.current) {
        imageFetchAbortRef.current.abort();
        imageFetchAbortRef.current = null;
      }
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    if (imageFetchAbortRef.current) {
      imageFetchAbortRef.current.abort();
    }
    imageFetchAbortRef.current = controller;

    setMediaLoadingProgress(0);

    const sourceUrl = resolveAssetUrl(backgroundValue);

    const fetchImage = async () => {
      try {
        const response = await fetch(sourceUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const contentLengthHeader = response.headers.get('content-length');
        const totalBytes = contentLengthHeader ? parseInt(contentLengthHeader, 10) : Number.NaN;

        if (!response.body) {
          if (isCancelled) return;
          const blob = await response.blob();
          imageBlobCache.set(backgroundValue, blob);
          revokeImageObjectUrl();
          const objectUrl = URL.createObjectURL(blob);
          imageObjectUrlRef.current = objectUrl;
          setImageSrc(objectUrl);
          setMediaLoadingProgress(1);
          imageFetchAbortRef.current = null;
          return;
        }

        const reader = response.body.getReader();
        const chunks: ArrayBuffer[] = [];
        let receivedBytes = 0;
        const hasTotal = Number.isFinite(totalBytes) && totalBytes > 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (isCancelled) return;
          if (value) {
            const chunk = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
            chunks.push(chunk);
            receivedBytes += value.byteLength;
            if (hasTotal) {
              setMediaLoadingProgress(Math.min(receivedBytes / totalBytes, 0.99));
            }
          }
        }

        if (isCancelled) return;

        const blob = new Blob(chunks);
        imageBlobCache.set(backgroundValue, blob);
        revokeImageObjectUrl();
        const objectUrl = URL.createObjectURL(blob);
        imageObjectUrlRef.current = objectUrl;
        setImageSrc(objectUrl);
        setMediaLoadingProgress(1);
        imageFetchAbortRef.current = null;
      } catch (error) {
        if (isCancelled) return;
        if ((error as Error).name === 'AbortError') return;
        console.error('Image prefetch error:', error);
        setImageSrc(null);
        setMediaLoadingProgress(null);
        imageFetchAbortRef.current = null;
      }
    };

    fetchImage();

    return () => {
      isCancelled = true;
      controller.abort();
      if (imageFetchAbortRef.current === controller) {
        imageFetchAbortRef.current = null;
      }
    };
  }, [backgroundType, backgroundValue]);

  useEffect(
    () => () => {
      if (imageFetchAbortRef.current) {
        imageFetchAbortRef.current.abort();
        imageFetchAbortRef.current = null;
      }
      revokeImageObjectUrl();
    },
    [],
  );

  const toggleMoving = () => setMoving((prev) => !prev);

  const normalizedProgress =
    typeof mediaLoadingProgress === 'number' ? Math.max(0, Math.min(mediaLoadingProgress, 1)) : null;

  const updateVideoProgress = () => {
    const video = videoRef.current;
    if (!video || !video.duration || Number.isNaN(video.duration) || video.duration === Infinity) {
      setMediaLoadingProgress((prev) => (prev === null ? 0 : prev));
      return;
    }

    const buffered = video.buffered;
    if (buffered.length === 0) {
      setMediaLoadingProgress((prev) => (prev === null ? 0 : prev));
      return;
    }

    const bufferedEnd = buffered.end(buffered.length - 1);
    const progress = Math.min(bufferedEnd / video.duration, 1);
    setMediaLoadingProgress(progress);
  };

  const handleVideoLoadStart = () => {
    setMediaReady(false);
    setMediaLoadingProgress(0);
  };

  const handleVideoLoadedData = () => {
    updateVideoProgress();
  };

  const handleVideoCanPlayThrough = () => {
    setMediaLoadingProgress(1);
    setMediaReady(true);
  };

  const handleVideoError = () => {
    setMediaLoadingProgress(null);
    setMediaReady(true);
  };

  const handleImageLoad = () => {
    setBgImageLoaded(true);
    setMediaLoadingProgress(1);
    setMediaReady(true);
  };

  const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    console.error('Image load error:', event);
    revokeImageObjectUrl();
    setImageSrc(null);
    setBgImageLoaded(false);
    setMediaLoadingProgress(null);
    setMediaReady(true);
  };

  return (
    <div className="relative w-full h-full border border-line-strong overflow-hidden">
      {backgroundType === 'video' && backgroundValue && (
        <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
          <video
            ref={videoRef}
            src={resolveAssetUrl(backgroundValue)}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className={`absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 transform object-cover transition-opacity duration-500 ${
              mediaReady ? 'opacity-100' : 'opacity-0'
            }`}
            onError={(event) => {
              console.error('Video loading error:', event);
              handleVideoError();
            }}
            onErrorCapture={() => console.log('Video error captured')}
            onLoadStart={handleVideoLoadStart}
            onProgress={updateVideoProgress}
            onLoadedData={handleVideoLoadedData}
            onCanPlayThrough={handleVideoCanPlayThrough}
          />
        </div>
      )}

      {backgroundType === 'image' && backgroundValue && (
        <>
          {bgImageLoaded ? (
            <img
              src={imageSrc ?? resolveAssetUrl(backgroundValue)}
              alt="Background"
              className={`absolute top-0 left-0 z-0 h-full w-full object-cover transition-opacity duration-500 ${
                mediaReady ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : (
            <img src={forest} alt="Forest fallback" className="absolute top-0 left-0 z-0 h-full w-full object-cover" />
          )}
        </>
      )}

      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 z-10 h-full w-full"
        style={{ display: 'block', backgroundColor: backgroundType === 'none' ? bgColor : 'transparent' }}
      />

      {!isPsychologist && normalizedProgress !== null && normalizedProgress < 1 && (
        <div className="absolute inset-x-0 bottom-0 z-30 h-[3px] bg-white/20">
          <div
            className="h-full bg-accent transition-[width] duration-200 ease-out"
            style={{ width: `${normalizedProgress * 100}%` }}
          />
        </div>
      )}

      {isPsychologist && (
        <div className="absolute inset-x-0 bottom-0 z-30">
          <DpdgControlPanel
            isMoving={moving}
            onToggleMoving={toggleMoving}
            trajectory={trajectory}
            changeTrajectory={changeTrajectory}
            loadingProgress={normalizedProgress ?? undefined}
            compact={compactControls}
          />
        </div>
      )}
    </div>
  );
};

export default DpDgBall;
